<script setup lang="ts">
import { GetOnboardingStatusDocument } from './OnboardingStatus.generated'

const { data } = useQuery({
  query: GetOnboardingStatusDocument,
  tags: ['OnboardingStatus'],
})

const { enabled: aiScanEnabled, isFetching: flagFetching } = useFlag('AI_SCAN')

interface Nudge {
  title: string
  description: string
  to: string
}

const nudge = computed<Nudge | null>(() => {
  const user = data.value?.user
  const org = data.value?.organization
  if (!user || flagFetching.value) return null

  const userIncomplete = !user.userOnboardingCompletedAt

  // AI scan orgs have a per-user only onboarding, so no org-level nudge.
  if (aiScanEnabled.value) {
    if (userIncomplete) {
      return {
        title: 'Finish setting up AI Insights',
        description: 'Add your role and install the scanning tool.',
        to: '/onboarding-wizard/ai-scan/role',
      }
    }
    return null
  }

  const isAdmin = user.permissions.canChangeOrg
  const orgIncomplete = isAdmin && !org?.orgOnboardingCompletedAt

  if (orgIncomplete) {
    return {
      title: 'Finish setting up your org',
      description: 'Configure GitHub, bring in skills, and get your team ready.',
      to: '/onboarding-wizard/org/familiarity',
    }
  }

  if (userIncomplete) {
    return {
      title: 'Set up skill delivery',
      description: 'Connect your AI tools so the right skills show up automatically.',
      to: '/onboarding-wizard/user/method',
    }
  }

  return null
})
</script>

<template>
  <NuxtLink
    v-if="nudge"
    :to="nudge.to"
    class="mx-2 flex flex-col gap-1 rounded-md border border-blue-300 bg-blue-100 px-3 py-2 text-left transition-colors hover:border-blue-400 hover:bg-blue-200 dark:border-blue-900 dark:bg-blue-950/40 dark:hover:border-blue-800 dark:hover:bg-blue-950/70"
  >
    <div class="flex items-center gap-2">
      <UIcon name="light:sparkles" class="size-4 shrink-0 text-blue-600 dark:text-blue-400" />
      <span class="text-sm font-semibold text-blue-900 dark:text-blue-100">{{ nudge.title }}</span>
    </div>
    <span class="text-xs text-blue-900/80 dark:text-blue-200/80">{{ nudge.description }}</span>
  </NuxtLink>
</template>
